import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Trash2, Copy, Check, FileText, Download, Image as ImageIcon } from "lucide-react";
import { api, imgUrl } from "@/lib/api";
import { PageTitle, Card, GoldButton } from "@/admin/ui";

const fmtSize = (b) => (b > 1024 * 1024 ? (b / 1024 / 1024).toFixed(1) + " MB" : Math.round(b / 1024) + " KB");

export const FilesManager = () => {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [copied, setCopied] = useState(null);
  const ref = useRef(null);

  const load = () => api.get("/admin/files").then(({ data }) => setFiles(data));
  useEffect(() => { load(); }, []);

  const upload = async (list) => {
    if (!list?.length) return;
    setUploading(true);
    try {
      for (const f of list) {
        const fd = new FormData(); fd.append("file", f);
        await api.post("/admin/files", fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success("Upload concluído"); await load();
    } catch { toast.error("Falha no upload"); } finally { setUploading(false); }
  };
  const remove = async (f) => { if (!window.confirm("Excluir arquivo?")) return; await api.delete(`/admin/files/${f.id}`); toast.success("Excluído"); load(); };
  const copy = (url) => { navigator.clipboard.writeText(url).catch(() => {}); setCopied(url); setTimeout(() => setCopied(null), 1500); };

  return (
    <div data-testid="admin-files">
      <PageTitle title="Arquivos" subtitle={`${files.length} arquivos`}
        action={<GoldButton onClick={() => ref.current?.click()} disabled={uploading} data-testid="file-upload"><Upload className="w-4 h-4 inline mr-1" /> {uploading ? "Enviando..." : "Upload"}</GoldButton>} />
      <input ref={ref} type="file" multiple className="hidden" onChange={(e) => upload(Array.from(e.target.files))} data-testid="file-input" />

      {files.length === 0 ? (
        <Card className="text-center py-16 text-white/40">Nenhum arquivo enviado.</Card>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {files.map((f) => {
            const url = `/api/files/${f.id}`;
            const isImg = (f.content_type || "").startsWith("image/");
            return (
              <Card key={f.id} className="flex items-center gap-3" data-testid={`file-${f.id}`}>
                <div className="w-14 h-14 rounded-xl overflow-hidden bg-white/5 flex items-center justify-center shrink-0">
                  {isImg ? <img src={imgUrl(url)} alt="" className="w-full h-full object-cover" /> : <FileText className="w-6 h-6 text-[#F5C542]" />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-white truncate">{f.filename}</div>
                  <div className="text-[11px] text-white/40">{fmtSize(f.size)}</div>
                  <div className="flex gap-1 mt-1">
                    <button onClick={() => copy(url)} className="flex items-center gap-1 text-xs text-white/60 hover:text-[#F5C542]">
                      {copied === url ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />} URL
                    </button>
                    <a href={imgUrl(url)} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-xs text-white/60 hover:text-[#F5C542] ml-2"><Download className="w-3 h-3" /> Abrir</a>
                  </div>
                </div>
                <button onClick={() => remove(f)} className="p-2 text-white/60 hover:text-[#FF3B30]"><Trash2 className="w-4 h-4" /></button>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};
